import { XCloseIcon } from "@vetty/icons";
import { Button } from "../button";
import { Checkbox } from "../checkbox";
import { Modal } from "../modal";
import styles from "./ExportModal.module.scss";

export type ExportModalOption = {
  key: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

type ExportModalProps = {
  isOpen: boolean;
  title: string;
  description: string;
  options: Array<ExportModalOption>;
  selectedKeys: Array<string>;
  isExporting?: boolean;
  exportLabel?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onToggleOption: (key: string, checked: boolean) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onExport: () => void;
};

export const ExportModal = ({
  isOpen,
  title,
  description,
  options,
  selectedKeys,
  isExporting = false,
  exportLabel = "Export CSV",
  errorMessage = null,
  onClose,
  onToggleOption,
  onSelectAll,
  onClearAll,
  onExport,
}: ExportModalProps) => {
  const selectedCount = selectedKeys.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles["comp"]}>
        <div className={styles["comp__header"]}>
          <div>
            <div className={styles["comp__heading"]}>{title}</div>
            <p className={styles["comp__description"]}>{description}</p>
          </div>

          <button
            type="button"
            className={styles["comp__close-button"]}
            onClick={onClose}
            aria-label="Close export modal"
          >
            <XCloseIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div className={styles["comp__toolbar"]}>
          <div className={styles["comp__selection-count"]}>
            {selectedCount} of {options.length} fields selected
          </div>

          <div className={styles["comp__toolbar-actions"]}>
            <Button
              size="small"
              variant="secondary"
              onClick={onSelectAll}
              disabled={isExporting}
            >
              Select all
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={onClearAll}
              disabled={isExporting}
            >
              Clear
            </Button>
          </div>
        </div>

        <div className={styles["comp__options"]}>
          {options.map((option) => (
            <div key={option.key} className={styles["comp__option"]}>
              <Checkbox
                id={`export-option-${option.key}`}
                variant="primary"
                checked={selectedKeys.includes(option.key)}
                disabled={option.disabled || isExporting}
                onChange={(checked) => onToggleOption(option.key, checked)}
              >
                <div className={styles["comp__option-content"]}>
                  <span className={styles["comp__option-label"]}>{option.label}</span>
                </div>
              </Checkbox>
            </div>
          ))}
        </div>

        {errorMessage && <div className={styles["comp__error"]}>{errorMessage}</div>}

        <div className={styles["comp__actions"]}>
          <Button variant="secondary" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onExport}
            loading={isExporting}
            disabled={!selectedCount || isExporting}
          >
            {exportLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
